import ClearIcon from '@mui/icons-material/Clear';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Checkbox,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React from 'react';

import {
  BoardFilters,
  BoardResponse,
  BoardSeverity,
  BoardState,
  BoardTrafficker,
} from '../api/ach/board';
import ContractCard from '../components/Alerts/ContractCard';
import ruleLabel from '../components/Alerts/ruleLabels';
import { SEVERITY_META, SEVERITY_ORDER } from '../components/Alerts/severity';
import { useAuth } from '../hooks/useAuth';
import { useAcknowledgeGroup, useAcknowledgeIssue, useBoard } from '../hooks/useBoard';
import useDebounce from '../hooks/useDebounce';

const PER_PAGE = 25;

const TABS: { state: BoardState; label: string }[] = [
  { state: 'open', label: 'Needs attention' },
  { state: 'acknowledged', label: 'In progress' },
  { state: 'resolved', label: 'Resolved' },
];

// Scope descriptor: My alerts (logged-in email), All alerts, a specific trafficker,
// or the Unassigned bucket. trafficker_email and trafficker_id are never both set.
type Scope =
  | { kind: 'mine' }
  | { kind: 'all' }
  | { kind: 'person'; trafficker: BoardTrafficker }
  | { kind: 'unassigned' };

function scopeFilters(scope: Scope, myEmail?: string): Partial<BoardFilters> {
  switch (scope.kind) {
    case 'mine':
      return myEmail ? { trafficker_email: myEmail } : {};
    case 'person':
      return { trafficker_id: scope.trafficker.id };
    case 'unassigned':
      return { trafficker_unassigned: true };
    default:
      return {};
  }
}

// traffickersInBoard collects the distinct assigned traffickers present in the
// response, for the person dropdown.
function traffickersInBoard(data?: BoardResponse): BoardTrafficker[] {
  const byId = new Map<number, BoardTrafficker>();
  data?.contracts.forEach((c) =>
    c.line_items.forEach((li) =>
      li.issues.forEach((i) => {
        if (i.trafficker) {
          byId.set(i.trafficker.id, i.trafficker);
        }
      })
    )
  );
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

// idFilter turns a raw numeric-input string into a filter value: a positive integer,
// or undefined (no filter) for empty / non-numeric / non-positive input.
function idFilter(raw: string): number | undefined {
  const n = Number(raw);
  return raw.trim() !== '' && Number.isInteger(n) && n > 0 ? n : undefined;
}

function scopeSelectValue(scope: Scope): string {
  if (scope.kind === 'person') {
    return `person:${scope.trafficker.id}`;
  }
  if (scope.kind === 'unassigned') {
    return 'unassigned';
  }
  return 'all';
}

// SeverityCard is a single-select filter card showing a severity label + faceted count.
function SeverityCard({
  label,
  count,
  color,
  selected,
  onClick,
}: {
  label: string;
  count: number;
  color?: 'error' | 'warning' | 'info' | 'default';
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: selected ? `${color ?? 'primary'}.main` : 'divider',
        borderWidth: selected ? 2 : 1,
        minWidth: 96,
      }}
    >
      <CardActionArea onClick={onClick} sx={{ p: 1 }} data-testid={`severity-card-${label.toLowerCase()}`}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h6">{count}</Typography>
      </CardActionArea>
    </Card>
  );
}

function AlertsPage() {
  const { user } = useAuth();
  const myEmail = user?.email;

  const [tab, setTab] = React.useState<BoardState>('open');
  const [severity, setSeverity] = React.useState<BoardSeverity | undefined>(undefined);
  const [scope, setScope] = React.useState<Scope>({ kind: 'mine' });
  const [ruleIds, setRuleIds] = React.useState<string[]>([]);
  const [campaignInput, setCampaignInput] = React.useState('');
  const [ioInput, setIoInput] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [fellBack, setFellBack] = React.useState(false);
  const autoFellBackRef = React.useRef(false);

  // Numeric id filters are debounced so each keystroke doesn't fire a board fetch.
  const campaignId = idFilter(useDebounce(campaignInput, 400));
  const insertionOrderId = idFilter(useDebounce(ioInput, 400));

  const filters: BoardFilters = React.useMemo(
    () => ({
      state: tab,
      severity,
      page,
      per_page: PER_PAGE,
      ...(ruleIds.length ? { rule_id: ruleIds } : {}),
      ...(campaignId ? { campaign_id: campaignId } : {}),
      ...(insertionOrderId ? { insertion_order_id: insertionOrderId } : {}),
      ...scopeFilters(scope, myEmail),
    }),
    [tab, severity, page, scope, myEmail, ruleIds, campaignId, insertionOrderId]
  );

  // A resolved debounced id filter is a new query — reset to the first page.
  React.useEffect(() => {
    setPage(1);
  }, [campaignId, insertionOrderId]);

  const { data, isLoading, isError, error, isFetching } = useBoard(filters);
  const ackIssue = useAcknowledgeIssue();
  const ackGroup = useAcknowledgeGroup();
  const acking = ackIssue.isLoading || ackGroup.isLoading;

  // Mine -> All auto-fallback: exactly once, when the default My view first resolves
  // to zero contracts, switch to All and surface a note.
  React.useEffect(() => {
    if (
      !autoFellBackRef.current &&
      scope.kind === 'mine' &&
      !isLoading &&
      !isError &&
      data &&
      data.pagination.total_contracts === 0
    ) {
      autoFellBackRef.current = true;
      setFellBack(true);
      setScope({ kind: 'all' });
      setPage(1);
    }
  }, [data, isLoading, isError, scope.kind]);

  const counts = data?.counts;
  const totalForSeverity = counts
    ? SEVERITY_ORDER.reduce((sum, s) => sum + counts.by_severity[s], 0)
    : 0;

  const traffickers = traffickersInBoard(data);
  const scopeValue = scopeSelectValue(scope);
  // Hide the whole control when the facet is absent (older ACH) or empty — no residue.
  const byRule = counts?.by_rule && Object.keys(counts.by_rule).length > 0 ? counts.by_rule : undefined;

  const onScopeSelect = (value: string) => {
    setPage(1);
    if (value === 'unassigned') {
      setScope({ kind: 'unassigned' });
    } else if (value.startsWith('person:')) {
      const id = Number(value.slice('person:'.length));
      const trafficker = traffickers.find((t) => t.id === id);
      setScope(trafficker ? { kind: 'person', trafficker } : { kind: 'all' });
    } else {
      setScope({ kind: 'all' });
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.pagination.total_contracts / data.pagination.per_page)) : 1;

  return (
    <Box p={2} data-testid="alerts-page">
      <Typography variant="h4" mb={2}>
        Alerts
      </Typography>

      {counts && counts.unacked_high_sev_over_24h > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="unacked-banner">
          {counts.unacked_high_sev_over_24h} high-severity alert(s) have been unacknowledged for over 24 hours.
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_e, v) => {
          setTab(v);
          setPage(1);
        }}
        sx={{ mb: 2 }}
      >
        {TABS.map((t) => (
          <Tab
            key={t.state}
            value={t.state}
            label={`${t.label}${counts ? ` (${counts.by_state[t.state]})` : ''}`}
          />
        ))}
      </Tabs>

      <Stack direction="row" spacing={1} flexWrap="wrap" mb={2} data-testid="severity-cards">
        <SeverityCard
          label="All"
          count={totalForSeverity}
          selected={severity === undefined}
          onClick={() => {
            setSeverity(undefined);
            setPage(1);
          }}
        />
        {SEVERITY_ORDER.map((sev) => (
          <SeverityCard
            key={sev}
            label={SEVERITY_META[sev].label}
            count={counts ? counts.by_severity[sev] : 0}
            color={SEVERITY_META[sev].color}
            selected={severity === sev}
            onClick={() => {
              setSeverity(sev);
              setPage(1);
            }}
          />
        ))}
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" mb={2}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={scope.kind === 'mine' ? 'mine' : 'all'}
          onChange={(_e, v) => {
            if (!v) {
              return;
            }
            setPage(1);
            setScope(v === 'mine' ? { kind: 'mine' } : { kind: 'all' });
          }}
        >
          <ToggleButton value="mine">My alerts</ToggleButton>
          <ToggleButton value="all">All alerts</ToggleButton>
        </ToggleButtonGroup>

        <Select
          size="small"
          value={scopeValue}
          onChange={(e) => onScopeSelect(String(e.target.value))}
          disabled={scope.kind === 'mine'}
          data-testid="person-select"
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="all">All people</MenuItem>
          <MenuItem value="unassigned">Unassigned</MenuItem>
          {traffickers.map((t) => (
            <MenuItem key={t.id} value={`person:${t.id}`}>
              {t.name}
            </MenuItem>
          ))}
        </Select>

        {byRule && (
          <Select
            multiple
            size="small"
            displayEmpty
            value={ruleIds}
            onChange={(e) => {
              const v = e.target.value;
              setRuleIds(typeof v === 'string' ? v.split(',') : v);
              setPage(1);
            }}
            renderValue={(selected) =>
              selected.length === 0 ? 'Issue type' : `Issue type (${selected.length})`
            }
            data-testid="issue-type-select"
            sx={{ minWidth: 200 }}
          >
            {Object.keys(byRule).map((ruleId) => (
              <MenuItem key={ruleId} value={ruleId} data-testid={`issue-type-option-${ruleId}`}>
                <Checkbox size="small" checked={ruleIds.includes(ruleId)} />
                <ListItemText primary={ruleLabel(ruleId)} secondary={ruleId} />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {byRule[ruleId]}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        )}

        <TextField
          size="small"
          type="number"
          label="Campaign ID"
          value={campaignInput}
          onChange={(e) => setCampaignInput(e.target.value)}
          data-testid="campaign-filter"
          sx={{ width: 150 }}
          InputProps={{
            endAdornment: campaignInput ? (
              <IconButton size="small" aria-label="Clear campaign filter" onClick={() => setCampaignInput('')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            ) : null,
          }}
        />

        <TextField
          size="small"
          type="number"
          label="IO ID"
          value={ioInput}
          onChange={(e) => setIoInput(e.target.value)}
          data-testid="io-filter"
          sx={{ width: 150 }}
          InputProps={{
            endAdornment: ioInput ? (
              <IconButton size="small" aria-label="Clear IO filter" onClick={() => setIoInput('')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            ) : null,
          }}
        />

        {isFetching && (
          <Typography variant="caption" color="text.secondary">
            Refreshing…
          </Typography>
        )}
      </Stack>

      {fellBack && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="fallback-note">
          No alerts are assigned to you — showing all alerts.
        </Alert>
      )}

      {isError && (
        <Alert severity="error" data-testid="board-error">
          Could not load alerts{error instanceof Error ? `: ${error.message}` : ''}.
        </Alert>
      )}

      {isLoading && <Typography color="text.secondary">Loading alerts…</Typography>}

      {!isLoading && !isError && data && data.contracts.length === 0 && (
        <Typography color="text.secondary" data-testid="empty-state">
          No alerts match the current filters.
        </Typography>
      )}

      {!isError &&
        data?.contracts.map((contract) => (
          <ContractCard
            key={contract.contract_id}
            contract={contract}
            readOnly={tab === 'resolved'}
            acking={acking}
            onAckIssue={(id) => ackIssue.mutate(id)}
            onAckGroup={(ruleId, contractId) => ackGroup.mutate({ rule_id: ruleId, contract_id: contractId })}
          />
        ))}

      {data && data.pagination.total_contracts > data.pagination.per_page && (
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" mt={2}>
          <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <Typography variant="body2">
            Page {data.pagination.page} of {totalPages}
          </Typography>
          <Button size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </Stack>
      )}
    </Box>
  );
}

export default AlertsPage;
